import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} Refs
 * @property {HTMLElement[]} videoLayers
 * @property {HTMLButtonElement[]} thumbs
 * @property {HTMLElement[]} cards
 *
 * @extends {Component<Refs>}
 */
class AxHeroComparator extends Component {
  requiredRefs = ['videoLayers', 'thumbs', 'cards'];

  #currentIndex = 0;
  #autoplayEnabled = false;
  #autoplayDelay = 7000;
  #isPaused = false;

  /** @type {number | null} */
  #autoplayTimer = null;

  /** @type {Map<HTMLVideoElement, () => void>} */
  #endedHandlers = new Map();

  /** @type {IntersectionObserver | null} */
  #lazyObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#autoplayEnabled = this.dataset.autoplay === 'true' && !prefersReducedMotion();
    this.#autoplayDelay = Number.parseInt(this.dataset.autoplayDelay ?? '7000', 10) || 7000;

    this.#bindThumbClicks();
    this.#bindVideoEnded();

    if (this.#autoplayEnabled) {
      document.addEventListener('visibilitychange', this.#onVisibilityChange);
    }

    this.#setupLazyVideoLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindThumbClicks();
    this.#unbindVideoEnded();

    this.#lazyObserver?.disconnect();
    this.#lazyObserver = null;

    document.removeEventListener('visibilitychange', this.#onVisibilityChange);

    this.#clearAutoplayTimer();
  }

  // Hold off on the initial play until the section is within 50px of the viewport.
  // The first slide's video is loaded on-demand by #playSlide → #loadVideoForSlide.
  // Subsequent slides only load when the user (or autoplay) navigates to them.
  #setupLazyVideoLoad() {
    const hasLazyVideos = this.querySelector('source[data-lazy-src]') !== null;

    if (!hasLazyVideos) {
      requestAnimationFrame(() => this.#playSlide(this.#currentIndex));
      return;
    }

    this.#lazyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#lazyObserver?.disconnect();
          this.#lazyObserver = null;
          requestAnimationFrame(() => this.#playSlide(this.#currentIndex));
          return;
        }
      },
      { rootMargin: '50px' }
    );

    this.#lazyObserver.observe(this);
  }

  /**
   * Load a single slide's video sources on demand.
   * No-op if already loaded (data-lazy-src has been swapped to src).
   * @param {number} index
   */
  #loadVideoForSlide(index) {
    const layer = this.refs.videoLayers[index];
    if (!layer) return;

    /** @type {NodeListOf<HTMLSourceElement>} */
    const sources = layer.querySelectorAll('source[data-lazy-src]');
    if (sources.length === 0) return;

    for (const source of sources) {
      const src = source.dataset.lazySrc;
      if (!src) continue;
      source.src = src;
      source.removeAttribute('data-lazy-src');
    }

    const video = layer.querySelector('video');
    video?.load();
  }

  #bindThumbClicks() {
    for (const thumb of this.refs.thumbs) {
      thumb.addEventListener('click', this.#onThumbClick);
    }
  }

  #unbindThumbClicks() {
    for (const thumb of this.refs.thumbs) {
      thumb.removeEventListener('click', this.#onThumbClick);
    }
  }

  /** @param {Event} event */
  #onThumbClick = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    const index = Number.parseInt(target.dataset.slideIndex ?? '0', 10);
    this.#goToSlide(index);
  };

  /**
   * @param {number} index
   */
  #goToSlide(index) {
    const { videoLayers, thumbs, cards } = this.refs;
    if (index === this.#currentIndex || index < 0 || index >= videoLayers.length) return;

    this.#clearAutoplayTimer();

    const prevVideo = videoLayers[this.#currentIndex]?.querySelector('video');
    prevVideo?.pause();

    videoLayers[this.#currentIndex]?.classList.remove('hero-comparator__video-layer--active');
    thumbs[this.#currentIndex]?.classList.remove('hero-comparator__thumb--active');
    thumbs[this.#currentIndex]?.setAttribute('aria-selected', 'false');
    cards[this.#currentIndex]?.classList.remove('hero-comparator__card--active');
    cards[this.#currentIndex]?.setAttribute('hidden', '');

    this.#currentIndex = index;

    videoLayers[index]?.classList.add('hero-comparator__video-layer--active');
    thumbs[index]?.classList.add('hero-comparator__thumb--active');
    thumbs[index]?.setAttribute('aria-selected', 'true');

    const card = cards[index];
    if (card) {
      card.classList.remove('hero-comparator__card--active');
      // Force reflow to restart the fade-in animation
      void card.offsetWidth;
      card.classList.add('hero-comparator__card--active');
      card.removeAttribute('hidden');
    }

    this.#playSlide(index);
  }

  /**
   * @param {number} index
   */
  #playSlide(index) {
    if (this.#isPaused) return;

    this.#loadVideoForSlide(index);

    const video = this.refs.videoLayers[index]?.querySelector('video');
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {
        // Autoplay blocked or video error — fall back to timer
        if (this.#autoplayEnabled) this.#scheduleAutoAdvance();
      });
      // Video slide with autoplay → `ended` listener advances next.
      // Video slide without autoplay → stays on last frame.
      return;
    }

    // Image slide → schedule advance only if autoplay is enabled
    if (this.#autoplayEnabled) this.#scheduleAutoAdvance();
  }

  #scheduleAutoAdvance() {
    this.#clearAutoplayTimer();
    this.#autoplayTimer = window.setTimeout(() => {
      this.#advanceToNext();
    }, this.#autoplayDelay);
  }

  #clearAutoplayTimer() {
    if (this.#autoplayTimer === null) return;
    clearTimeout(this.#autoplayTimer);
    this.#autoplayTimer = null;
  }

  #advanceToNext() {
    const next = (this.#currentIndex + 1) % this.refs.videoLayers.length;
    this.#goToSlide(next);
  }

  #pauseAutoplay = () => {
    if (this.#isPaused) return;
    this.#isPaused = true;
    const video = this.refs.videoLayers[this.#currentIndex]?.querySelector('video');
    video?.pause();
    this.#clearAutoplayTimer();
  };

  #resumeAutoplay = () => {
    if (!this.#isPaused) return;
    this.#isPaused = false;
    this.#playSlide(this.#currentIndex);
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#pauseAutoplay();
    } else {
      this.#resumeAutoplay();
    }
  };

  #bindVideoEnded() {
    for (const [index, layer] of this.refs.videoLayers.entries()) {
      const video = layer.querySelector('video');
      if (!video) continue;
      const handler = () => this.#handleVideoEnded(index);
      video.addEventListener('ended', handler);
      this.#endedHandlers.set(video, handler);
    }
  }

  #unbindVideoEnded() {
    for (const [video, handler] of this.#endedHandlers) {
      video.removeEventListener('ended', handler);
    }
    this.#endedHandlers.clear();
  }

  /**
   * @param {number} index
   */
  #handleVideoEnded(index) {
    if (index !== this.#currentIndex) return;
    if (!this.#autoplayEnabled) return;
    this.#advanceToNext();
  }
}

if (!customElements.get('ax-hero-comparator')) {
  customElements.define('ax-hero-comparator', AxHeroComparator);
}
